import React, { createContext, useContext } from 'react';
import { View, Text, TouchableOpacity, Platform, Animated, StyleSheet } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { LinearGradient } from 'expo-linear-gradient';
import * as Haptics from 'expo-haptics';

// ══════════════════════════════════════════════════════════════════════════
// TabBarVisibilityContext — rapatrié ici (au lieu d'un fichier séparé
// contexts/TabBarVisibilityContext.tsx) parce que ce dernier n'était pas
// résolu par Metro en environnement Vercel, malgré vérification du chemin,
// de la casse, du commit et du cache. En le mettant dans ce fichier — dont
// on sait avec certitude qu'il est résolu correctement puisque
// CustomTabBar est déjà importé ailleurs sans erreur — on élimine le
// chemin d'import suspect. app/(tabs)/_layout.tsx et
// app/(tabs)/market/_layout.tsx importent maintenant tous les deux
// TabBarVisibilityContext / useTabBarVisibility depuis '@/components/CustomTabBar'.
//
// FIX build cassé ("useTabBarVisibility is not a function") : cette
// section était prévue mais absente du fichier réellement déployé — seuls
// CustomTabBar et TabBarButtonConfig étaient exportés. Réappliquée ici.
// ══════════════════════════════════════════════════════════════════════════

type TabBarVisibilityContextType = {
  // onDone (optionnel) est appelé quand l'animation de cache/affichage est
  // RÉELLEMENT terminée (pas au moment de l'appel) — utilisé par
  // market/_layout.tsx pour séquencer : cacher la navbar parente d'abord,
  // puis seulement afficher la sienne une fois confirmé que la première a
  // fini de disparaître.
  setTabBarHidden: (hidden: boolean, onDone?: () => void) => void;
};

export const TabBarVisibilityContext = createContext<TabBarVisibilityContextType>({
  setTabBarHidden: () => {},
});

export const useTabBarVisibility = () => useContext(TabBarVisibilityContext);

// ══════════════════════════════════════════════════════════════════════════
// CustomTabBar — extrait de app/(tabs)/_layout.tsx pour être réutilisé tel
// quel par la navbar principale ET par la sous-navbar Market
// (app/(tabs)/market/_layout.tsx), avec le même style (dégradé violet/or,
// indicateur doré, bande "hors ligne" inline, animation show/hide, gestion
// de l'ombre iOS/Android/web).
//
// Seule différence fonctionnelle par rapport à l'original :
// - TAB_CONFIG (constante figée) devient buttonsConfig (prop), pour que
//   chaque appelant définisse ses propres boutons.
// - Ajout de interceptKeys / onInterceptPress : pour un bouton dont la clé
//   ne correspond à aucun Tabs.Screen interne au stack courant (ex: le
//   bouton "home" dans la sous-navbar Market, qui doit sortir du stack
//   Market plutôt que de naviguer en interne), on appelle onInterceptPress
//   à la place de navigation.navigate(). Tout le reste du comportement
//   (haptics, offline strip, gestion de l'ombre) est inchangé.
// ══════════════════════════════════════════════════════════════════════════

export type TabBarButtonConfig = {
  key: string;
  icon: string;
  iconActive: string;
};

type CustomTabBarProps = {
  state: any;
  navigation: any;
  isOnline: boolean;
  tabBarTranslateY: Animated.Value;
  tabBarShadowOpacity: Animated.Value;
  isTabBarFullyHidden: boolean;
  onMeasureHeight?: (height: number) => void;
  buttonsConfig: TabBarButtonConfig[];
  onInterceptPress?: (key: string) => void;
  interceptKeys?: string[];
};

export default function CustomTabBar({
  state,
  navigation,
  isOnline,
  tabBarTranslateY,
  tabBarShadowOpacity,
  isTabBarFullyHidden,
  onMeasureHeight,
  buttonsConfig,
  onInterceptPress,
  interceptKeys = [],
}: CustomTabBarProps) {
  const handleTabPress = (routeName: string, index: number) => {
    if (Platform.OS !== 'web') {
      Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Light);
    }

    // Cas spécial : un bouton "intercepté" (ex: Home dans la sous-navbar
    // Market) ne correspond à aucun Tabs.Screen interne à ce stack — on
    // appelle le callback fourni par le parent au lieu de naviguer.
    if (interceptKeys.includes(routeName)) {
      onInterceptPress?.(routeName);
      return;
    }

    const isFocused = state.index === index;
    const event = navigation.emit({
      type: 'tabPress',
      target: state.routes[index].key,
      canPreventDefault: true,
    });
    if (!isFocused && !event.defaultPrevented) {
      navigation.navigate(routeName);
    }
  };

  // Contenu interne factorisé — identique que l'ombre soit montée ou non
  const tabBarContent = (
    <>
      {!isOnline && (
        <View style={styles.navOfflineStrip}>
          <View style={styles.navOfflineDot} />
          <Text style={styles.navOfflineStripText}>Hors ligne</Text>
        </View>
      )}

      <LinearGradient
        colors={['rgba(138, 43, 226, 0.98)', 'rgba(75, 0, 130, 0.98)']}
        style={styles.tabsGradient}
      >
        <View style={styles.tabs}>
          {buttonsConfig.map((config) => {
            // On cherche la route correspondante dans state.routes UNIQUEMENT
            // pour savoir si ce bouton est "actif" (route courante) et pour
            // récupérer sa clé React stable. Un bouton intercepté (ex: "home")
            // n'aura jamais de route associée — routeIndex reste -1, et
            // isFocused reste toujours false pour lui, ce qui est le
            // comportement voulu (Home n'est jamais "actif").
            const routeIndex = state.routes.findIndex((r: any) => r.name === config.key);
            const isFocused = routeIndex !== -1 && state.index === routeIndex;

            return (
              <TouchableOpacity
                key={config.key}
                style={styles.tab}
                onPress={() => handleTabPress(config.key, routeIndex)}
                activeOpacity={0.7}
              >
                <Ionicons
                  name={(isFocused ? config.iconActive : config.icon) as any}
                  size={26}
                  color={isFocused ? '#FFD700' : '#E0D0FF'}
                />
                {isFocused && <View style={styles.activeIndicator} />}
              </TouchableOpacity>
            );
          })}
        </View>
      </LinearGradient>
    </>
  );

  return (
    // ── Conteneur EXTÉRIEUR : porte l'ombre (iOS/Android/web), ne bouge
    // JAMAIS lui-même. C'est ce qui empêche l'ombre de "traîner" ou de
    // laisser un résidu visuel à l'ancienne position : elle est toujours
    // rattachée à la zone réelle en bas d'écran, jamais à une position
    // translatée.
    //
    // IMPORTANT : sur Android, `elevation` génère une ombre au niveau du
    // compositeur natif qui n'est PAS toujours supprimée par `opacity: 0`
    // (bug de plateforme connu) — d'où le résidu semi-transparent qui
    // "fenêtre" sur le contenu en dessous même à opacité nulle. Le seul
    // moyen fiable de faire disparaître totalement l'ombre est de
    // démonter physiquement la View qui la porte, plutôt que de l'animer
    // en opacité seule.
    //
    // `isTabBarFullyHidden` ne devient true qu'APRÈS la fin de l'animation
    // de translation (voir setTabBarHidden dans le layout parent) — on ne
    // démonte donc jamais l'ombre en plein milieu du mouvement, seulement
    // une fois la barre réellement hors écran.
    isTabBarFullyHidden ? (
      <Animated.View
        style={[styles.bottomTabsContainer, { transform: [{ translateY: tabBarTranslateY }] }]}
        onLayout={e => onMeasureHeight?.(e.nativeEvent.layout.height)}
        pointerEvents="box-none"
      >
        {tabBarContent}
      </Animated.View>
    ) : (
      <Animated.View style={[styles.bottomTabsShadowWrap, { opacity: tabBarShadowOpacity }]} pointerEvents="box-none">
        <Animated.View
          style={[styles.bottomTabsContainer, { transform: [{ translateY: tabBarTranslateY }] }]}
          onLayout={e => onMeasureHeight?.(e.nativeEvent.layout.height)}
        >
          {tabBarContent}
        </Animated.View>
      </Animated.View>
    )
  );
}

// ─── STYLES ───────────────────────────────────────────────────────────────
// Repris à l'identique depuis app/(tabs)/_layout.tsx original.
const styles = StyleSheet.create({
  bottomTabsShadowWrap: {
    ...Platform.select({
      ios: {
        position: 'absolute' as any,
        bottom: 0, left: 0, right: 0,
        shadowColor:   '#000',
        shadowOffset:  { width: 0, height: -4 },
        shadowOpacity: 0.15,
        shadowRadius:  8,
      },
      android: {
        position:  'absolute' as any,
        bottom: 0, left: 0, right: 0,
        elevation: 12,
      },
      web: {
        position: 'absolute' as any,
        bottom: 0, left: 0, right: 0,
        boxShadow: '0 -4px 12px rgba(0, 0, 0, 0.18)' as any,
        zIndex: 50,
      },
    }),
  },

  bottomTabsContainer: {
    position: 'absolute' as any,
    bottom: 0, left: 0, right: 0,
  },

  navOfflineStrip: {
    backgroundColor: '#B71C1C',
    flexDirection:   'row',
    alignItems:      'center',
    justifyContent:  'center',
    paddingVertical: 3,
  },
  navOfflineDot: {
    width: 6, height: 6, borderRadius: 3,
    backgroundColor: '#FF5252', marginRight: 5,
  },
  navOfflineStripText: {
    color: '#FFCDD2', fontSize: 11, fontWeight: '600', letterSpacing: 0.5,
  },

  tabsGradient: {
    paddingBottom: Platform.OS === 'ios' ? 30 : 12,
    paddingTop: 8,
  },

  tabs: {
    flexDirection: 'row',
    justifyContent: 'space-around',
    alignItems: 'center',
    paddingVertical: 8,
  },

  tab: {
    alignItems: 'center',
    justifyContent: 'center',
    position: 'relative',
    paddingVertical: 8,
    paddingHorizontal: 12,
    minWidth: 60,
  },

  activeIndicator: {
    position: 'absolute',
    bottom: -4,
    width: 35, height: 4,
    backgroundColor: '#FFD700',
    borderRadius: 2,
    shadowColor: '#FFD700',
    shadowOffset: { width: 0, height: 0 },
    shadowOpacity: 0.8,
    shadowRadius: 4,
    elevation: 4,
  },
});
